import { useLayoutEffect, useRef, useState } from "react";
import { Badge } from "components/ui/badge";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "components/ui/tooltip";

const GAP_PX = 4;

interface OverflowBadgesProps {
  items: string[];
  formatLabel?: (item: string) => string;
}

export const OverflowBadges = ({
  items,
  formatLabel = (x) => x,
}: OverflowBadgesProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const measureRef = useRef<HTMLDivElement>(null);
  const [visibleCount, setVisibleCount] = useState(items.length);

  useLayoutEffect(() => {
    const container = containerRef.current;
    const measure = measureRef.current;
    if (!container || !measure) return;

    const compute = () => {
      const available = container.clientWidth;
      const badges = Array.from(measure.children) as HTMLElement[];
      const itemBadges = badges.slice(0, items.length);
      const overflowBadge = badges[items.length];
      if (itemBadges.length === 0) {
        setVisibleCount(0);
        return;
      }

      const totalWidth = itemBadges.reduce(
        (sum, el, i) => sum + el.offsetWidth + (i > 0 ? GAP_PX : 0),
        0,
      );
      if (totalWidth <= available) {
        setVisibleCount(items.length);
        return;
      }

      const overflowWidth = overflowBadge?.offsetWidth ?? 0;
      let used = 0;
      let count = 0;
      for (let i = 0; i < itemBadges.length; i++) {
        const w = itemBadges[i].offsetWidth + (i > 0 ? GAP_PX : 0);
        const remaining = items.length - (i + 1);
        const reserve = remaining > 0 ? overflowWidth + GAP_PX : 0;
        if (used + w + reserve > available) break;
        used += w;
        count++;
      }
      setVisibleCount(count);
    };

    compute();
    const observer = new ResizeObserver(compute);
    observer.observe(container);
    return () => observer.disconnect();
  }, [items]);

  const hidden = items.slice(visibleCount);

  return (
    <div ref={containerRef} className="relative w-full overflow-hidden">
      <div
        ref={measureRef}
        aria-hidden
        className="pointer-events-none invisible absolute flex gap-1"
      >
        {items.map((item) => (
          <Badge key={item} variant="outline">
            {formatLabel(item)}
          </Badge>
        ))}
        <Badge variant="outline">{"+" + items.length}</Badge>
      </div>
      <div className="flex gap-1">
        {items.slice(0, visibleCount).map((item) => (
          <Badge key={item} variant="outline">
            {formatLabel(item)}
          </Badge>
        ))}
        {hidden.length > 0 && (
          <Tooltip>
            <TooltipTrigger asChild>
              <span className="inline-flex h-5 w-fit shrink-0 items-center justify-center whitespace-nowrap rounded-full border border-border bg-input/20 px-2 py-0.5 text-xs font-medium text-foreground dark:bg-input/30">
                {"+" + hidden.length}
              </span>
            </TooltipTrigger>
            <TooltipContent>
              <ul className="flex flex-col gap-0.5">
                {hidden.map((item) => (
                  <li key={item}>{formatLabel(item)}</li>
                ))}
              </ul>
            </TooltipContent>
          </Tooltip>
        )}
      </div>
    </div>
  );
};
